'use client';
import Avo from '@susu/custom-made/Avo';
import { ThemeContext } from '@susu/custom-made/providers';
import { Clients, Level } from '@susu/custom-made/utils/constants';
import type {
  LayerDefinitions,
  OptionsSelection,
  StylePresets,
  typeOptionValue,
  typeTranslations,
} from '@susu/custom-made/utils/interfaces';
import {
  changeSection,
  getConfigurationTypeBySection,
  getSectionByConfigurationType,
  Section,
} from '@susu/custom-made/utils/navigation';
import type { ConfigurationTypes, ProductType } from '@susu/custom-made/utils/productDefinition';
import { Products } from '@susu/custom-made/utils/productDefinition';
import {
  getAvoEventParams,
  getConfigurationTypeText,
  isAdditonalProduct,
  isExtraConfigurationInProduct,
} from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import Button, { ButtonSize, ButtonType } from '../button/button';
import type { PreviewProps } from '../preview/preview';
import Preview from '../preview/preview';
import PreviewSlider from '../previewSlider/previewSlider';
import Toast, { ToastType } from '../toast/toast';
import styles from './previewHeader.module.scss';
import { getExtraProductPreviewLayers, getProductPreviewLayers } from '@susu/custom-made/utils/preview/layers';

export default function PreviewHeader({
  translations,
  layerDefinitions,
  optionValues,
  stylePresets,
  product,
  hasMissingSelection,
  toastBtnClickHandler,
  isFirstRender,
  defaultLayers,
  isExtraProduct,
}: Readonly<{
  translations: typeTranslations;
  layerDefinitions: LayerDefinitions;
  optionValues: typeOptionValue;
  stylePresets: StylePresets;
  product: ProductType;
  hasMissingSelection: boolean;
  toastBtnClickHandler: () => void;
  isFirstRender: boolean;
  defaultLayers: Map<string, PreviewProps>;
  isExtraProduct: boolean;
}>) {
  const { dispatch, state } = useContext(ThemeContext);
  const [imageTemplates, setImageTemplates] = useState<Map<string, PreviewProps> | undefined>(undefined);
  const [sliderTogoIndex, setSliderTogoIndex] = useState<number | null>(null);
  const [isBaseStyleSelectionDone, setIsBaseStyleSelectionDone] = useState<boolean>(false);
  const [allOptionsAreSelected, setAllOptionsAreSelected] = useState<Boolean>(false);
  const [missingSelectionFlow, setMissingSelectionFlow] = useState<Boolean>(false);

  const productLayers = useMemo(() => imageTemplates ?? defaultLayers, [imageTemplates]);

  const goToSummary = () => {
    changeSection(Section.Summary, Level.Group, dispatch);
    state.client === Clients.onLine && Avo.custommadeDone(getAvoEventParams(state));
  };

  const setSliderSelection = useCallback(() => {
    let imgIndex = 0;
    let isIndexSet = false;

    if (isExtraProduct) {
      setSliderTogoIndex(0);
      return;
    }

    state.product &&
      Products[state.product].forEach(configurationType => {
        if (getSectionByConfigurationType(configurationType) === state.section) {
          isIndexSet = true;
          setSliderTogoIndex(imgIndex);
        }
        imgIndex++;
      });

    if (state.section === Section.Fabric) {
      setSliderTogoIndex(0);
    } else if (!isIndexSet && imgIndex > 0) {
      setSliderTogoIndex(imgIndex - 1);
    }
  }, [state.product, state.section]);

  useEffect(() => {
    if (
      layerDefinitions &&
      optionValues &&
      state.selectedFabric &&
      state.selectedOptions &&
      state.section &&
      !isFirstRender
    ) {
      const selectedFabric = state.selectedFabric;
      const selectedOptions: OptionsSelection = structuredClone(state.selectedOptions);

      if (isExtraProduct) {
        getExtraProductPreviewLayers(
          layerDefinitions,
          optionValues,
          selectedFabric,
          selectedOptions,
          state.hiddenOptionsAndValues,
          getConfigurationTypeBySection(state.section),
        ).then(productLayers => {
          setImageTemplates(productLayers);
        });
      } else {
        getProductPreviewLayers(
          layerDefinitions,
          optionValues,
          selectedFabric,
          selectedOptions,
          state.hiddenOptionsAndValues,
          product,
        ).then(productLayers => {
          setImageTemplates(productLayers);
        });
      }
    }

    setSliderSelection();
  }, [
    layerDefinitions,
    optionValues,
    state.selectedFabric,
    state.selectedOptions,
    state.section,
    state.isAdditionalProductAdded,
    setSliderSelection,
    state.hiddenOptionsAndValues.optionsPerConfiguration,
    state.hiddenOptionsAndValues.optionValuesPerConfiguration,
    isFirstRender,
  ]);

  useEffect(() => {
    let baseStyleSelectionDone = true;
    Products[product].forEach(configurationTypeId => {
      if (
        stylePresets.configurationTypes[configurationTypeId] &&
        !isExtraConfigurationInProduct(product, configurationTypeId) &&
        !(
          state.selectedBaseStyle[configurationTypeId] ||
          (isAdditonalProduct(product, configurationTypeId) && !state.isAdditionalProductAdded[configurationTypeId])
        )
      ) {
        baseStyleSelectionDone = false;
      }
    });

    setIsBaseStyleSelectionDone(baseStyleSelectionDone);
  }, [state.selectedBaseStyle, product, state.isAdditionalProductAdded, stylePresets]);

  useEffect(() => {
    if (hasMissingSelection) {
      setMissingSelectionFlow(true);
    }
    if (!hasMissingSelection && missingSelectionFlow) {
      setAllOptionsAreSelected(true);
      setTimeout(() => {
        setAllOptionsAreSelected(false);
      }, 3000);
    }
  }, [hasMissingSelection, missingSelectionFlow]);

  return (
    <div className={styles.previewContainer}>
      <PreviewSlider sliderTogoIndex={sliderTogoIndex}>
        {Array.from(productLayers).map(([key, value], index) => {
          return (
            <Fragment key={index}>
              {!isExtraProduct &&
                isAdditonalProduct(state.product!, key as ConfigurationTypes) &&
                !state.isAdditionalProductAdded[key] && (
                  <div className={cn(styles.overlay, styles[`index-${index}`])}>
                    <div className={styles.overlayText}>
                      {`No ${getConfigurationTypeText(key as ConfigurationTypes).toLowerCase()} added yet`}
                    </div>
                  </div>
                )}
              <Preview
                key={key}
                configurationType={key as ConfigurationTypes}
                presetIndex={index}
                layers={value.layers}
                fallbackImage={value.fallbackImage}
                isZoomable={true}
              />
            </Fragment>
          );
        })}
      </PreviewSlider>
      <div className={styles.textContainer}>
        <div className={styles.priceContainer} data-test-id="price">
          {translations.total} {state.selectedFabric?.prices?.symbol}
          {state.totalPrice}
        </div>
        {state.level === Level.Group && state.deliveryPeriod && (
          <div className={styles.deliveryContainer} data-test-id="delivery">
            {`${state.deliveryPeriod.minDurationInWeeks}-${state.deliveryPeriod.maxDurationInWeeks} ${translations.weekDelivery}`}
          </div>
        )}
      </div>
      {state.level === Level.Group && (
        <div className={styles.buttonContainer}>
          <Button
            onClick={goToSummary}
            testId="save-btn"
            isDisabled={hasMissingSelection}
            type={isBaseStyleSelectionDone ? ButtonType.Primary : ButtonType.Secondary}
            size={ButtonSize.Small}
            noBorder={true}
          >
            Finish
          </Button>
        </div>
      )}
      <div className={styles.toastContainer}>
        {hasMissingSelection && (
          <Toast
            type={ToastType.Warning}
            btnText="View"
            text="Update unavailable options"
            hasBtn={true}
            icon={'alert'}
            isHidden={false}
            btnClickHandler={toastBtnClickHandler}
          />
        )}
        <Toast
          type={ToastType.Regular}
          btnText="View"
          text="All options are updated"
          hasBtn={false}
          icon={'checkmark'}
          isHidden={!allOptionsAreSelected}
          btnClickHandler={toastBtnClickHandler}
        />
      </div>
    </div>
  );
}
